'use client';

import React from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle
} from '@ui/components/ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@ui/components/ui/sheet';

import { useMainContentRef } from '../../hooks/useMainContentRef';
import { LocationPickerContent } from './LocationPickerContent';

interface LocationPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationSelect: (location: { latitude: number; longitude: number; name: string }) => void;
  title?: string;
}

export const LocationPickerModal: React.FC<LocationPickerModalProps> = ({
  isOpen,
  onClose,
  onLocationSelect,
  title
}) => {
  const mainElement = useMainContentRef();
  // If we don't have access to the main element yet or we're in a server context, use the Sheet component as fallback
  if (!mainElement) {
    return (
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent
          className="
            fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
            bg-white rounded-lg max-h-[90vh] overflow-y-auto 
            w-full max-w-lg p-0 shadow-xl
            data-[state=open]:animate-in data-[state=closed]:animate-out
            data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0
            data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95
            data-[state=open]:duration-300 data-[state=closed]:duration-200
          "
          style={
            {
              '--tw-bg-opacity': '0.5',
              '--tw-backdrop-blur': '6px'
            } as React.CSSProperties
          }
        >
          <SheetTitle className="sr-only">{title || 'Location Picker'}</SheetTitle>
          <SheetDescription className="sr-only">Select a location</SheetDescription>
          <LocationPickerContent onLocationSelect={onLocationSelect} />
        </SheetContent>
      </Sheet>
    );
  }

  // Use the custom dialog component that portals to the main element
  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogPortal container={mainElement}>
        <DialogOverlay className="fixed inset-0 z-50 bg-neutral-900/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription className="sr-only">Location picker</DialogDescription>
          </DialogHeader>
          <LocationPickerContent onLocationSelect={onLocationSelect} />
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
